import type { CadDocument, Layer } from '../model/document';
import { entityBox, type BlockDef, type CadEntity } from '../model/entity';
import { boxCenter, type Pt } from '../model/geom';
import type { Micron } from '../model/units';

/**
 * 도면 비교 (작업지시서 P4-09)
 *
 * ★ 리뉴얼 업무의 90%는 "뭘 몇 개 빼고 몇 개 넣는가" 다.
 *   (`고도화_제안_v2.md` 5.1절)
 *   그 답을 내는 것이 이 파일이고, P5-05 물량 산출이 그대로 올라탄다.
 *
 * ── 왜 단순 add/remove 로 나누면 안 되는가 ─────────────────────────────
 *
 *   곤돌라 하나를 2m 옮긴 것을 "철거 1 + 신설 1" 로 집계하면
 *   견적이 두 배로 부풀고, 실제로는 **이설**이라 비용이 훨씬 싸다.
 *   그래서 이동(moved)을 별도 분류로 둔다. 이것이 이 비교 엔진의 존재 이유다.
 *
 * ── 대응 전략 ─────────────────────────────────────────────────────────
 *
 *   두 도면이 **id 를 공유하는지**로 전략이 갈린다.
 *
 *   · id 가 하나라도 겹치면 → **id 만 믿는다.**
 *     버전을 분기해서 만든 도면이므로 id 는 보존된다.
 *     이때 id 가 다른 객체는 사용자가 실제로 지우고 새로 넣은 것이다.
 *
 *   · id 가 전혀 겹치지 않으면 → **모양으로 짝짓는다.**
 *     도면을 다시 업로드한 경우다. id 는 전부 새로 붙으므로 쓸모가 없다.
 *
 *   ⚠️ 이 분기가 없으면 실무에서 견적이 틀린다.
 *      분기 버전에서 "곤돌라 c 철거 + 곤돌라 d 신설" 을 했는데
 *      모양이 같다는 이유로 "이설 1건" 으로 뭉뚱그리면
 *      철거비와 자재비가 통째로 누락된다. 테스트로 잡은 실제 버그다.
 */

export type ChangeKind = 'added' | 'removed' | 'moved' | 'modified' | 'unchanged';

export interface MovedPair {
  before: CadEntity;
  after: CadEntity;
  dx: Micron;
  dy: Micron;
  /** 이동 거리 (µm) */
  distance: number;
}

export interface ModifiedPair {
  before: CadEntity;
  after: CadEntity;
  /** 무엇이 바뀌었는지 — 화면에 그대로 보여준다 */
  changes: string[];
}

/** 무엇을 기준으로 짝지었는지. 화면에 표시해 사용자가 결과를 해석할 수 있게 한다. */
export type MatchStrategy = 'id' | 'shape';

export interface DocumentDiff {
  added: CadEntity[];
  removed: CadEntity[];
  moved: MovedPair[];
  modified: ModifiedPair[];
  unchanged: CadEntity[];
  layers: {
    added: Layer[];
    removed: Layer[];
    changed: { before: Layer; after: Layer }[];
  };
  /** 실제로 사용된 대응 전략 */
  strategy: MatchStrategy;
}

export interface DiffOptions {
  /**
   * 'auto'  — id 가 겹치면 id, 아니면 모양 (기본값)
   * 'id'    — id 만. 다시 업로드한 도면에는 쓰지 말 것
   * 'shape' — 모양만. id 를 무시하고 강제로 이동을 찾는다
   */
  matchBy?: 'auto' | 'id' | 'shape';
}

/** 이동으로 볼 최대 거리. 이보다 멀면 다른 물건으로 본다. */
const MAX_MOVE_UM = 50_000_000; // 50m

/** 이 거리 이하는 "안 움직인 것"으로 본다 (µm 반올림 오차 흡수) */
const MOVE_EPSILON_UM = 100; // 0.1mm

export function diffDocuments(
  before: CadDocument,
  after: CadDocument,
  options: DiffOptions = {}
): DocumentDiff {
  const beforeBlocks = new Map(before.blocks.map((b) => [b.code, b]));
  const afterBlocks = new Map(after.blocks.map((b) => [b.code, b]));

  const beforeById = new Map(before.entities.map((e) => [e.id, e]));
  const afterById = new Map(after.entities.map((e) => [e.id, e]));

  // id 가 하나라도 겹치면 같은 계보의 도면이다 → id 를 믿는다
  let sharedIds = 0;
  for (const id of afterById.keys()) if (beforeById.has(id)) sharedIds++;

  const mode = options.matchBy ?? 'auto';
  const strategy: MatchStrategy =
    mode === 'id' ? 'id' : mode === 'shape' ? 'shape' : sharedIds > 0 ? 'id' : 'shape';

  const diff: DocumentDiff = {
    added: [],
    removed: [],
    moved: [],
    modified: [],
    unchanged: [],
    layers: diffLayers(before.layers, after.layers),
    strategy,
  };

  const matchedBefore = new Set<string>();
  const matchedAfter = new Set<string>();

  /* ---- 1단계: id 로 짝짓기 ---- */
  if (strategy === 'id') {
    for (const [id, a] of afterById) {
      const b = beforeById.get(id);
      if (!b) continue;

      matchedBefore.add(id);
      matchedAfter.add(id);
      classifyPair(b, a, beforeBlocks, afterBlocks, diff);
    }
  }

  const leftoverBefore = before.entities.filter((e) => !matchedBefore.has(e.id));
  const leftoverAfter = after.entities.filter((e) => !matchedAfter.has(e.id));

  /* ---- id 를 믿는 경우: 남은 것은 그대로 신설/철거다 ----
     여기서 모양으로 다시 짝지으면 "지우고 새로 넣은 것"이
     "이설"로 뭉개져 철거비·자재비가 통째로 누락된다. */
  if (strategy === 'id') {
    diff.added.push(...leftoverAfter);
    diff.removed.push(...leftoverBefore);
    return diff;
  }

  /* ---- 2단계: 모양으로 짝짓기 (재업로드 도면) ---- */

  // 지문 → 후보 목록. 같은 모양이 여러 개면 가장 가까운 것끼리 붙인다.
  const pool = new Map<string, CadEntity[]>();
  for (const e of leftoverBefore) {
    const key = shapeFingerprint(e, beforeBlocks);
    const arr = pool.get(key);
    if (arr) arr.push(e);
    else pool.set(key, [e]);
  }

  for (const a of leftoverAfter) {
    const key = shapeFingerprint(a, afterBlocks);
    const candidates = pool.get(key);
    if (!candidates || candidates.length === 0) {
      diff.added.push(a);
      continue;
    }

    const aCenter = boxCenter(entityBox(a, afterBlocks));
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < candidates.length; i++) {
      const c = boxCenter(entityBox(candidates[i]!, beforeBlocks));
      const d = Math.hypot(aCenter.x - c.x, aCenter.y - c.y);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }

    if (bestDist > MAX_MOVE_UM) {
      // 너무 멀면 짝이 아니다 — 철거하고 새로 놓은 것으로 본다
      diff.added.push(a);
      continue;
    }

    const b = candidates.splice(bestIdx, 1)[0]!;
    classifyPair(b, a, beforeBlocks, afterBlocks, diff);
  }

  // 짝을 못 찾고 남은 before 는 전부 철거
  for (const list of pool.values()) {
    for (const e of list) diff.removed.push(e);
  }

  return diff;
}

/* -------------------------------------------------------------------------- *
 * 쌍 분류
 * -------------------------------------------------------------------------- */

function classifyPair(
  b: CadEntity,
  a: CadEntity,
  beforeBlocks: Map<string, BlockDef>,
  afterBlocks: Map<string, BlockDef>,
  diff: DocumentDiff
): void {
  const changes = describeChanges(b, a);

  const bc = boxCenter(entityBox(b, beforeBlocks));
  const ac = boxCenter(entityBox(a, afterBlocks));
  const dx = ac.x - bc.x;
  const dy = ac.y - bc.y;
  const dist = Math.hypot(dx, dy);

  const sameShape =
    shapeFingerprint(b, beforeBlocks) === shapeFingerprint(a, afterBlocks);

  if (sameShape && dist <= MOVE_EPSILON_UM && changes.length === 0) {
    diff.unchanged.push(a);
    return;
  }

  if (sameShape && changes.length === 0) {
    diff.moved.push({ before: b, after: a, dx, dy, distance: dist });
    return;
  }

  diff.modified.push({ before: b, after: a, changes });
}

/**
 * 무엇이 바뀌었는지 사람 말로.
 * 위치 변화는 여기서 다루지 않는다 — 이동/변경을 가르는 판단은 호출자가 한다.
 */
function describeChanges(b: CadEntity, a: CadEntity): string[] {
  const out: string[] = [];

  if (b.type !== a.type) {
    out.push(`종류 변경: ${b.type} → ${a.type}`);
    return out;
  }
  if (b.layer !== a.layer) out.push(`레이어: ${b.layer} → ${a.layer}`);
  if ((b.color ?? null) !== (a.color ?? null)) out.push('색상 변경');
  if ((b.lineWeight ?? null) !== (a.lineWeight ?? null)) out.push('선 두께 변경');

  if (b.type === 'circle' && a.type === 'circle' && b.radius !== a.radius) {
    out.push(`반지름: ${b.radius / 1000}mm → ${a.radius / 1000}mm`);
  }
  if (b.type === 'arc' && a.type === 'arc') {
    if (b.radius !== a.radius) out.push(`반지름: ${b.radius / 1000}mm → ${a.radius / 1000}mm`);
    if (Math.abs(b.startAngle - a.startAngle) > 1e-9 || Math.abs(b.endAngle - a.endAngle) > 1e-9) {
      out.push('호 범위 변경');
    }
  }
  if (b.type === 'text' && a.type === 'text') {
    if (b.text !== a.text) out.push(`문자: "${b.text}" → "${a.text}"`);
    if (b.height !== a.height) out.push('글자 크기 변경');
  }
  if (b.type === 'polyline' && a.type === 'polyline') {
    if (b.points.length !== a.points.length) {
      out.push(`정점 ${b.points.length} → ${a.points.length}개`);
    } else if (!sameRelativeShape(b.points, a.points)) {
      out.push('형상 변경');
    }
    if (b.closed !== a.closed) out.push(b.closed ? '열림으로 변경' : '닫힘으로 변경');
  }
  if (b.type === 'line' && a.type === 'line') {
    const bl = Math.hypot(b.b.x - b.a.x, b.b.y - b.a.y);
    const al = Math.hypot(a.b.x - a.a.x, a.b.y - a.a.y);
    if (Math.abs(bl - al) > MOVE_EPSILON_UM) {
      out.push(`길이: ${Math.round(bl / 1000)}mm → ${Math.round(al / 1000)}mm`);
    }
  }
  if (b.type === 'blockref' && a.type === 'blockref') {
    if (b.blockCode !== a.blockCode) out.push(`규격: ${b.blockCode} → ${a.blockCode}`);
    if (Math.abs(b.rotation - a.rotation) > 1e-9) out.push('방향 변경');
    if (b.scale !== a.scale) out.push('배율 변경');

    const bAttrs = userAttrs(b.attributes);
    const aAttrs = userAttrs(a.attributes);
    for (const k of new Set([...Object.keys(bAttrs), ...Object.keys(aAttrs)])) {
      if (bAttrs[k] !== aAttrs[k]) {
        out.push(`${k}: ${bAttrs[k] ?? '(없음)'} → ${aAttrs[k] ?? '(없음)'}`);
      }
    }
  }

  return out;
}

function userAttrs(attrs: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(attrs)) if (!k.startsWith('p:')) out[k] = v;
  return out;
}

/** 정점들의 상대 배치가 같은가 (평행이동은 같은 것으로 본다) */
function sameRelativeShape(a: Pt[], b: Pt[]): boolean {
  if (a.length !== b.length || a.length === 0) return false;
  const dx = b[0]!.x - a[0]!.x;
  const dy = b[0]!.y - a[0]!.y;
  for (let i = 1; i < a.length; i++) {
    if (Math.abs(b[i]!.x - a[i]!.x - dx) > MOVE_EPSILON_UM) return false;
    if (Math.abs(b[i]!.y - a[i]!.y - dy) > MOVE_EPSILON_UM) return false;
  }
  return true;
}

/* -------------------------------------------------------------------------- *
 * 기하 지문 — 위치를 뺀 "모양"
 * -------------------------------------------------------------------------- */

/**
 * 위치에 무관한 모양 식별자.
 * 이것이 같고 위치만 다르면 "이동"이다.
 */
export function shapeFingerprint(e: CadEntity, blocks: Map<string, BlockDef>): string {
  const box = entityBox(e, blocks);
  const w = box.maxX - box.minX;
  const h = box.maxY - box.minY;

  switch (e.type) {
    case 'line': {
      const len = Math.round(Math.hypot(e.b.x - e.a.x, e.b.y - e.a.y));
      const ang = Math.round(Math.atan2(e.b.y - e.a.y, e.b.x - e.a.x) * 1000);
      return `line|${e.layer}|${len}|${ang}`;
    }
    case 'polyline': {
      // 첫 정점 기준 상대 좌표 — 평행이동에 불변
      const p0 = e.points[0]!;
      const rel = e.points.map((p) => `${p.x - p0.x},${p.y - p0.y}`).join(';');
      return `poly|${e.layer}|${e.closed}|${rel}`;
    }
    case 'circle':
      return `circle|${e.layer}|${e.radius}`;
    case 'arc':
      return `arc|${e.layer}|${e.radius}|${Math.round(e.startAngle * 1000)}|${Math.round(e.endAngle * 1000)}`;
    case 'text':
      return `text|${e.layer}|${e.text}|${e.height}|${Math.round(e.rotation * 1000)}`;
    case 'blockref': {
      const attrs = Object.entries(userAttrs(e.attributes))
        .sort(([x], [y]) => x.localeCompare(y))
        .map(([k, v]) => `${k}=${v}`)
        .join(',');
      return `block|${e.layer}|${e.blockCode}|${Math.round(e.rotation * 1000)}|${e.scale}|${attrs}`;
    }
    case 'dimension':
      return `dim|${e.layer}|${e.kind}|${w}x${h}`;
  }
}

/* -------------------------------------------------------------------------- *
 * 레이어 비교
 * -------------------------------------------------------------------------- */

function diffLayers(before: Layer[], after: Layer[]): DocumentDiff['layers'] {
  const b = new Map(before.map((l) => [l.code, l]));
  const a = new Map(after.map((l) => [l.code, l]));

  const added = after.filter((l) => !b.has(l.code));
  const removed = before.filter((l) => !a.has(l.code));
  const changed: { before: Layer; after: Layer }[] = [];

  for (const [code, la] of a) {
    const lb = b.get(code);
    if (!lb) continue;
    if (
      lb.color !== la.color ||
      lb.visible !== la.visible ||
      lb.locked !== la.locked ||
      lb.lineWeight !== la.lineWeight ||
      lb.lineType !== la.lineType
    ) {
      changed.push({ before: lb, after: la });
    }
  }

  return { added, removed, changed };
}

/* -------------------------------------------------------------------------- *
 * 요약
 * -------------------------------------------------------------------------- */

export interface DiffSummary {
  addedCount: number;
  removedCount: number;
  movedCount: number;
  modifiedCount: number;
  unchangedCount: number;
  /** 하나라도 바뀐 것이 있는가 */
  hasChanges: boolean;
}

export function summarize(diff: DocumentDiff): DiffSummary {
  const s: DiffSummary = {
    addedCount: diff.added.length,
    removedCount: diff.removed.length,
    movedCount: diff.moved.length,
    modifiedCount: diff.modified.length,
    unchangedCount: diff.unchanged.length,
    hasChanges: false,
  };
  s.hasChanges =
    s.addedCount + s.removedCount + s.movedCount + s.modifiedCount > 0 ||
    diff.layers.added.length + diff.layers.removed.length + diff.layers.changed.length > 0;
  return s;
}

/** 변경 종류별 화면 표기 — 색상만으로 구분하지 않도록 아이콘을 함께 준다 */
export const CHANGE_META: Record<ChangeKind, { label: string; icon: string; color: string }> = {
  added: { label: '신설', icon: '+', color: 'var(--sc-ok)' },
  removed: { label: '철거', icon: '−', color: 'var(--sc-danger)' },
  moved: { label: '이설', icon: '→', color: 'var(--sc-warn)' },
  modified: { label: '변경', icon: '~', color: 'var(--sc-accent)' },
  unchanged: { label: '유지', icon: '·', color: 'var(--one-text-muted)' },
};
